// The ride graphic: a 1080 px wide card for an Instagram story (1920 tall) or post (1350 tall).
// The route as a white line, the ride's name and day, four measured numbers, the Musette mark.
// Musette's own layout: left aligned, plain labels over the numbers, no badges or borrowed marks.
import { frame, proxied, tileUrl, type P } from "./tiles";

export type Size = "story" | "post";
export type BgId = "own" | "ride" | "map" | "ink" | "yellow" | "night" | "forest" | "steel";
export type Stat = { label: string; value: string; unit?: string };
export type GraphicSpec = {
  size: Size; bg: BgId; title: string; sub: string; pts: P[]; stats: Stat[]; foot?: string | null; left?: string | null; profile?: [number, number][] | null;
  photo?: CanvasImageSource & { width: number; height: number } | null;
};

export const BGS: { id: BgId; label: string }[] = [
  { id: "own", label: "Your photo" }, { id: "ride", label: "Ride photo" }, { id: "map", label: "Map" },
  { id: "steel", label: "Chrome" }, { id: "yellow", label: "Yellow" }, { id: "night", label: "Night" }, { id: "forest", label: "Forest" }, { id: "ink", label: "Ink" },
];
// Dusty Yellow is the app's accent; Night is the band's night end.
const FLAT: Record<string, string> = { ink: "#0d0f12", yellow: "#e6c25e", night: "#111628", forest: "#1f4a37" };
export const W = 1080;
export const heightOf = (s: Size) => (s === "story" ? 1920 : 1350);

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((ok, no) => { const i = new Image(); i.crossOrigin = "anonymous"; i.decoding = "async"; i.onload = () => ok(i); i.onerror = no; i.src = src; });
}

const FONT = "'Instrument Sans', system-ui, -apple-system, sans-serif";
export async function fontsReady() {
  try { await Promise.all(["500 120px", "600 40px", "400 34px", "500 26px"].map((f) => document.fonts.load(`${f} "Instrument Sans"`))); } catch { /* falls back to the system face */ }
}

/** Text with tracking, drawn glyph by glyph (canvas letterSpacing is not on every Safari). */
function spaced(c: CanvasRenderingContext2D, t: string, x: number, y: number, track: number, align: "left" | "right" = "left") {
  const w = [...t].reduce((a, ch) => a + c.measureText(ch).width + track, -track);
  let at = align === "right" ? x - w : x;
  for (const ch of t) { c.fillText(ch, at, y); at += c.measureText(ch).width + track; }
}

function wrap(c: CanvasRenderingContext2D, t: string, max: number, lines = 2): string[] {
  const out: string[] = []; let line = "";
  for (const word of t.split(/\s+/).filter(Boolean)) {
    const next = line ? `${line} ${word}` : word;
    if (c.measureText(next).width <= max || !line) line = next; else { out.push(line); line = word; }
  }
  if (line) out.push(line);
  if (out.length > lines) { const keep = out.slice(0, lines); keep[lines - 1] = keep[lines - 1].replace(/\s*\S*$/, "") + "…"; return keep; }
  return out;
}

function cover(c: CanvasRenderingContext2D, img: { width: number; height: number } & CanvasImageSource, w: number, h: number) {
  const k = Math.max(w / img.width, h / img.height), iw = img.width * k, ih = img.height * k;
  c.drawImage(img, (w - iw) / 2, (h - ih) / 2, iw, ih);
}

function steel(c: CanvasRenderingContext2D, w: number, h: number) {
  // The app icon's corner flare: pale steel under everything that carries ink, colour flaring from
  // the top right corner. No dark corner here: on a tall card it read as a smudge by the mark.
  const g = c.createLinearGradient(w * 0.15, 0, w * 0.85, h);
  [[0, "#f6f9fb"], [0.22, "#dde4ea"], [0.4, "#fbfcfd"], [0.58, "#ccd5dc"], [0.78, "#eef2f5"], [1, "#d3dae1"]].forEach(([o, col]) => g.addColorStop(o as number, col as string));
  c.fillStyle = g; c.fillRect(0, 0, w, h);
  // A faint oil-slick sheen, not a rainbow: cold tints at a third strength or less, blended.
  const f = c.createRadialGradient(w, 0, 0, w, 0, 520);
  [[0, "rgba(255,255,255,0.95)"], [0.18, "rgba(206,196,240,0.34)"], [0.34, "rgba(176,206,238,0.30)"], [0.5, "rgba(192,230,222,0.22)"], [0.64, "rgba(236,226,206,0.12)"], [0.82, "rgba(255,255,255,0)"]].forEach(([o, col]) => f.addColorStop(o as number, col as string));
  c.fillStyle = f; c.fillRect(0, 0, w, h);
  const gl = c.createLinearGradient(w, 0, 0, h);
  [[0.14, "rgba(255,255,255,0)"], [0.21, "rgba(255,255,255,0.7)"], [0.25, "rgba(255,255,255,0)"]].forEach(([o, col]) => gl.addColorStop(o as number, col as string));
  c.fillStyle = gl; c.fillRect(0, 0, w, h);
}

function shade(c: CanvasRenderingContext2D, w: number, h: number, top: number, bottom: number) {
  let g = c.createLinearGradient(0, 0, 0, h * 0.42); g.addColorStop(0, `rgba(0,0,0,${top})`); g.addColorStop(1, "rgba(0,0,0,0)");
  c.fillStyle = g; c.fillRect(0, 0, w, h * 0.42);
  g = c.createLinearGradient(0, h * 0.48, 0, h); g.addColorStop(0, "rgba(0,0,0,0)"); g.addColorStop(1, `rgba(0,0,0,${bottom})`);
  c.fillStyle = g; c.fillRect(0, h * 0.48, w, h * 0.52);
}

/** The height profile as a slim silhouette: distance along, metres up. */
function profileStrip(c: CanvasRenderingContext2D, pts: [number, number][], x: number, y: number, w: number, h: number, ink: string, light: boolean) {
  const km = pts[pts.length - 1][0] || 1, lo = Math.min(...pts.map((p) => p[1])), hi = Math.max(...pts.map((p) => p[1])), span = Math.max(60, hi - lo);
  const px = (k: number) => x + (k / km) * w, py = (m: number) => y + (1 - (m - lo) / span) * h;
  const line = new Path2D(); pts.forEach((p, i) => (i ? line.lineTo(px(p[0]), py(p[1])) : line.moveTo(px(p[0]), py(p[1]))));
  const area = new Path2D(line); area.lineTo(px(km), y + h); area.lineTo(x, y + h); area.closePath();
  c.save(); c.fillStyle = light ? "rgba(11,12,14,0.12)" : "rgba(255,255,255,0.16)"; c.fill(area);
  c.strokeStyle = ink; c.globalAlpha = 0.75; c.lineWidth = 3; c.lineJoin = "round"; c.stroke(line); c.restore();
}

export async function drawGraphic(canvas: HTMLCanvasElement, s: GraphicSpec) {
  const H = heightOf(s.size), story = s.size === "story", M = 88;
  canvas.width = W; canvas.height = H;
  const c = canvas.getContext("2d")!;
  const light = s.bg === "steel" || s.bg === "yellow";
  const ink = light ? "#0b0c0e" : "#ffffff", soft = light ? "rgba(11,12,14,0.66)" : "rgba(255,255,255,0.74)", rule = light ? "rgba(11,12,14,0.28)" : "rgba(255,255,255,0.34)";
  const box = story ? { x: M + 20, y: 490, w: W - (M + 20) * 2, h: 640 } : { x: M + 60, y: 345, w: W - (M + 60) * 2, h: 335 };
  const fr = frame(s.pts, W, H, box, -1);

  // Ground
  if ((s.bg === "own" || s.bg === "ride") && s.photo) { cover(c, s.photo, W, H); c.fillStyle = "rgba(0,0,0,0.12)"; c.fillRect(0, 0, W, H); shade(c, W, H, 0.6, 0.82); }
  else if (s.bg === "map") {
    c.fillStyle = "#0e1013"; c.fillRect(0, 0, W, H);
    const imgs = await Promise.all(fr.tiles.map((t) => loadImage(proxied(tileUrl(t, true))).then((i) => [t, i] as const).catch(() => null)));
    for (const x of imgs) if (x) c.drawImage(x[1], x[0].px, x[0].py, x[0].size + 1, x[0].size + 1);
    c.fillStyle = "rgba(8,10,12,0.18)"; c.fillRect(0, 0, W, H); shade(c, W, H, 0.55, 0.85);
  }
  else if (s.bg === "steel") steel(c, W, H);
  else {
    c.fillStyle = FLAT[s.bg] ?? FLAT.ink; c.fillRect(0, 0, W, H);
    const g = c.createRadialGradient(W * 0.2, 0, 0, W * 0.2, 0, H * 0.9); g.addColorStop(0, "rgba(255,255,255,0.10)"); g.addColorStop(1, "rgba(255,255,255,0)");
    c.fillStyle = g; c.fillRect(0, 0, W, H);
  }

  // Top line: the wordmark and the kind of ride
  c.fillStyle = ink; c.textBaseline = "alphabetic";
  c.font = `600 40px ${FONT}`; spaced(c, "musette", M, story ? 150 : 120, -1.2);
  c.fillStyle = soft; c.font = `500 24px ${FONT}`; spaced(c, "RIDE", W - M, story ? 146 : 116, 0.5, "right");

  // Title and day
  c.fillStyle = ink; c.font = `500 96px ${FONT}`;
  const tl = wrap(c, s.title || "Ride", W - M * 2);
  const ty = story ? 290 : 230;
  tl.forEach((l, i) => spaced(c, l, M, ty + i * 98, -3));
  c.fillStyle = soft; c.font = `400 34px ${FONT}`;
  c.fillText(s.sub, M, ty + (tl.length - 1) * 98 + 62);

  // The route
  const path = new Path2D(); s.pts.forEach((p, i) => { const [x, y] = fr.at(p); if (i) path.lineTo(x, y); else path.moveTo(x, y); });
  c.save(); c.lineJoin = "round"; c.lineCap = "round";
  if (!light) { c.shadowColor = "rgba(0,0,0,0.38)"; c.shadowBlur = 18; }
  c.strokeStyle = s.bg === "map" ? "#f1f4f7" : ink; c.lineWidth = 9; c.stroke(path); c.restore();
  const [sx, sy] = fr.at(s.pts[0]), [ex, ey] = fr.at(s.pts[s.pts.length - 1]);
  c.save(); c.lineWidth = 6; c.strokeStyle = ink; c.fillStyle = s.bg === "yellow" ? FLAT.yellow : light ? "#f6f9fb" : (s.bg === "map" ? "#0e1013" : FLAT[s.bg] ?? "#0e1013");
  c.beginPath(); c.arc(sx, sy, 15, 0, Math.PI * 2); c.fill(); c.stroke();
  if (Math.hypot(ex - sx, ey - sy) > 30) { c.fillStyle = ink; c.beginPath(); c.arc(ex, ey, 9, 0, Math.PI * 2); c.fill(); }
  c.restore();

  // Numbers: two rows of two, a hairline over each row
  const rows = story ? [1200, 1420] : [716, 896], colX = [M, W / 2 + 20], vs = story ? 120 : 104, vy = story ? 186 : 164;
  rows.forEach((ry, ri) => {
    c.fillStyle = rule; c.fillRect(M, ry, W - M * 2, 2);
    s.stats.slice(ri * 2, ri * 2 + 2).forEach((st, ci) => {
      const x = colX[ci];
      c.fillStyle = soft; c.font = `500 26px ${FONT}`; spaced(c, st.label.toUpperCase(), x, ry + 58, 0.5);
      c.fillStyle = ink; c.font = `500 ${vs}px ${FONT}`; spaced(c, st.value, x, ry + vy, -4);
      if (st.unit) { const vw = [...st.value].reduce((a, ch) => a + c.measureText(ch).width - 4, 4); c.fillStyle = soft; c.font = `500 40px ${FONT}`; c.fillText(st.unit, x + vw + 12, ry + vy); }
    });
  });

  // Foot: the height profile across the width, then the bike on the left and Paris on the right
  const fy = H - (story ? 110 : 84);
  if (s.profile && s.profile.length > 1) profileStrip(c, s.profile, M, fy - (story ? 130 : 116), W - M * 2, story ? 70 : 50, ink, light);
  c.font = `500 30px ${FONT}`;
  if (s.left) { c.fillStyle = soft; c.fillText(s.left, M, fy); }
  if (s.foot) { c.fillStyle = ink; c.textAlign = "right"; c.fillText(s.foot, W - M, fy); c.textAlign = "left"; }
}

export async function graphicFile(canvas: HTMLCanvasElement, name: string): Promise<File> {
  const blob: Blob = await new Promise((ok, no) => canvas.toBlob((b) => (b ? ok(b) : no(new Error("No image"))), "image/png"));
  return new File([blob], `${name}.png`, { type: "image/png" });
}
